(() => {
  const ICONS = {
    playstation: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 18V5.8c0-1.8 1.2-3 3-3 2.4 0 4 1.3 4 3.5v6.1"/><path d="M14 8.5c3.4-.6 5 .1 5 1.4 0 1.5-2.5 2.5-6 2.8M7 12.6c-3.2.7-5 1.7-5 3 0 1.7 3.1 2.1 7.2 1.2l1.8-.4"/></svg>',
    xbox: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M7.7 6.7c1.3-.8 2.7-1.2 4.3-1.2s3 .4 4.3 1.2M6.4 16.6c1.5-3.1 3.3-5.5 5.6-7.3 2.3 1.8 4.1 4.2 5.6 7.3"/></svg>',
    nintendo: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="5"/><path d="M10 4v16M15.5 8.5h.01M17.5 11h.01M6 11h2M7 10v2"/></svg>',
    controller: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8.5 8h7a5 5 0 0 1 4.8 3.6l1 3.5a3 3 0 0 1-5.1 2.9l-1.4-1.5H9.2L7.8 18a3 3 0 0 1-5.1-2.9l1-3.5A5 5 0 0 1 8.5 8Z"/><path d="M7 11v4M5 13h4M16.5 12h.01M18.5 14h.01"/></svg>',
    console: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="2.5" width="14" height="19" rx="2"/><path d="M8 7h8M8 10h5M16 17h.01"/></svg>',
    tools: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14.7 6.3 3-3a4.2 4.2 0 0 1-5.4 5.4l-7.8 7.8a2.1 2.1 0 1 0 3 3l7.8-7.8a4.2 4.2 0 0 0 5.4-5.4l-3 3-3-3Z"/></svg>'
  };

  class RepairBooking extends HTMLElement {
    constructor() {
      super();
      this.panel = this.querySelector('[data-panel]');
      this.errorBox = this.querySelector('[data-error]');
      this.dataNode = this.querySelector('[data-repair-booking-data]');
      this.copyNode = this.querySelector('[data-repair-booking-copy]');
      this.deviceDataNode = this.querySelector('[data-repair-device-data]');
      this.addonDataNode = this.querySelector('[data-repair-addon-data]');
      this.progressItems = [...this.querySelectorAll('[data-progress-step]')];
      this.stepOrder = ['platform', 'device', 'symptom', 'results', 'details'];
      this.state = {
        platform: null,
        device: null,
        query: '',
        repairId: null,
        results: []
      };
      this.repairs = [];
      this.devices = [];
      this.addons = [];
      this.copy = {};
    }

    connectedCallback() {
      if (!this.dataNode || !this.panel) return;

      try {
        this.repairs = JSON.parse(this.dataNode.textContent || '[]');
        this.devices = this.deviceDataNode ? JSON.parse(this.deviceDataNode.textContent || '[]') : [];
        this.addons = this.addonDataNode ? JSON.parse(this.addonDataNode.textContent || '[]') : [];
        this.copy = this.copyNode ? JSON.parse(this.copyNode.textContent || '{}') : {};
      } catch (error) {
        console.error('ABXONE repair finder data could not be parsed.', error);
        this.showError('Der Reparaturfinder konnte nicht geladen werden.');
        return;
      }

      this.progressItems.forEach((item) => {
        item.addEventListener('click', () => {
          if (!item.classList.contains('is-done')) return;
          this.goToStep(item.dataset.progressStep);
        });
      });

      this.renderPlatforms();
    }

    normalize(value) {
      return String(value || '')
        .toLocaleLowerCase('de-DE')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ß/g, 'ss')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
    }

    splitTerms(value) {
      return String(value || '')
        .split(/[\n,;|]+/)
        .map((item) => item.trim())
        .filter(Boolean);
    }

    money(cents) {
      try {
        return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format((Number(cents) || 0) / 100);
      } catch (_) {
        return `${((Number(cents) || 0) / 100).toFixed(2).replace('.', ',')} €`;
      }
    }

    uniqueBy(items, keyFn) {
      const map = new Map();
      items.forEach((item) => {
        const key = keyFn(item);
        if (!map.has(key)) map.set(key, item);
      });
      return [...map.values()];
    }

    escape(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    copyRaw(key, fallback, replacements = {}) {
      let text = String(this.copy?.[key] || fallback || '');
      Object.entries(replacements).forEach(([token, value]) => {
        text = text.split(`{${token}}`).join(String(value ?? ''));
      });
      return text;
    }

    copyText(key, fallback, replacements = {}) {
      return this.escape(this.copyRaw(key, fallback, replacements));
    }

    setProgress(step) {
      const currentIndex = this.stepOrder.indexOf(step);
      this.progressItems.forEach((item) => {
        const index = this.stepOrder.indexOf(item.dataset.progressStep);
        item.classList.toggle('is-active', index === currentIndex);
        item.classList.toggle('is-done', index < currentIndex);
        item.disabled = index >= currentIndex;
        if (index === currentIndex) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
    }

    goToStep(step) {
      if (step === 'platform') {
        this.state = { platform: null, device: null, query: '', repairId: null, results: [] };
        this.renderPlatforms();
      } else if (step === 'device' && this.state.platform) {
        this.state.device = null;
        this.state.query = '';
        this.state.repairId = null;
        this.state.results = [];
        this.renderDevices();
      } else if (step === 'symptom' && this.state.platform && this.state.device) {
        this.state.repairId = null;
        this.state.results = [];
        this.renderSymptomSearch(this.state.query);
      } else if (step === 'results' && this.state.results.length) {
        this.state.repairId = null;
        this.renderResults();
      }
    }

    stepHead(title, subtitle, backStep = null) {
      return `
        <div class="repair-booking__step-head">
          <div>
            <h2 class="h3">${this.escape(title)}</h2>
            ${subtitle ? `<p>${this.escape(subtitle)}</p>` : ''}
          </div>
          ${backStep ? `<button type="button" class="repair-booking__back" data-back="${backStep}">← Zurück</button>` : ''}
        </div>`;
    }

    bindBack() {
      this.panel.querySelectorAll('[data-back]').forEach((button) => {
        button.addEventListener('click', () => this.goToStep(button.dataset.back));
      });
    }

    repairPool() {
      return this.repairs.filter((repair) => repair.platform === this.state.platform && repair.device === this.state.device);
    }

    renderPlatforms() {
      this.clearError();
      this.setProgress('platform');
      const platforms = this.uniqueBy(this.repairs, (r) => this.normalize(r.platform));

      this.panel.innerHTML = `
        ${this.stepHead('Welche Plattform?', 'Wähle zuerst die Plattform deines Geräts.')}
        <div class="repair-booking__grid">
          ${platforms.map((item) => {
            const count = this.uniqueBy(this.repairs.filter((r) => r.platform === item.platform), (r) => this.normalize(r.device)).length;
            return `
              <button type="button" class="repair-booking__choice" data-platform="${this.escape(item.platform)}">
                <span class="repair-booking__choice-icon">${ICONS[item.platformIcon] || ICONS.console}</span>
                <span class="repair-booking__choice-title">${this.escape(item.platform)}</span>
                <span class="repair-booking__choice-meta">${count} ${count === 1 ? 'Gerät' : 'Geräte'}</span>
              </button>`;
          }).join('')}
        </div>`;

      this.panel.querySelectorAll('[data-platform]').forEach((button) => {
        button.addEventListener('click', () => {
          this.state.platform = button.dataset.platform;
          this.state.device = null;
          this.state.query = '';
          this.state.repairId = null;
          this.state.results = [];
          this.renderDevices();
        });
      });
    }

    renderDevices() {
      this.clearError();
      this.setProgress('device');
      const filtered = this.repairs.filter((r) => r.platform === this.state.platform);
      const devices = this.uniqueBy(filtered, (r) => this.normalize(r.device));

      this.panel.innerHTML = `
        ${this.stepHead('Welches Gerät?', this.state.platform, 'platform')}
        <div class="repair-booking__grid">
          ${devices.map((item) => {
            const repairCount = filtered.filter((r) => r.device === item.device).length;
            return `
              <button type="button" class="repair-booking__choice" data-device="${this.escape(item.device)}">
                <span class="repair-booking__choice-icon">${ICONS[item.platformIcon] || ICONS.console}</span>
                <span class="repair-booking__choice-title">${this.escape(item.device)}</span>
                <span class="repair-booking__choice-meta">${repairCount} ${repairCount === 1 ? 'Service' : 'Services'}</span>
              </button>`;
          }).join('')}
        </div>`;

      this.bindBack();
      this.panel.querySelectorAll('[data-device]').forEach((button) => {
        button.addEventListener('click', () => {
          this.state.device = button.dataset.device;
          this.state.query = '';
          this.state.repairId = null;
          this.state.results = [];
          this.renderSymptomSearch();
        });
      });
    }

    getQuickSymptoms() {
      const deviceConfig = this.devices.find((item) =>
        this.normalize(item.platform) === this.normalize(this.state.platform) &&
        this.normalize(item.device) === this.normalize(this.state.device)
      );

      if (deviceConfig) {
        return this.uniqueBy(
          this.splitTerms(deviceConfig.quickSymptoms),
          (term) => this.normalize(term)
        ).slice(0, 12);
      }

      // Backwards compatibility for themes that used V4 before device-level lists existed.
      const legacy = [];
      this.repairPool().forEach((repair) => {
        this.splitTerms(repair.quickSymptoms).forEach((term) => legacy.push(term));
      });
      return this.uniqueBy(legacy, (term) => this.normalize(term)).slice(0, 12);
    }

    renderSymptomSearch(existingQuery = '') {
      this.clearError();
      this.setProgress('symptom');
      const quickSymptoms = this.getQuickSymptoms();

      this.panel.innerHTML = `
        ${this.stepHead('Was ist das Problem?', `${this.state.platform} · ${this.state.device}`, 'device')}
        <div class="repair-booking__search-wrap">
          <form class="repair-booking__search" data-search-form>
            <label class="repair-booking__search-label" for="RepairSearch-${this.dataset.sectionId}">Fehler oder Symptom eingeben</label>
            <div class="repair-booking__search-row">
              <input
                class="repair-booking__search-input"
                id="RepairSearch-${this.dataset.sectionId}"
                name="q"
                type="search"
                value="${this.escape(existingQuery)}"
                autocomplete="off"
                placeholder="z. B. kein Bild, Stick Drift, geht nicht an …"
              >
              <button class="button button--primary repair-booking__search-button" type="submit">Reparatur finden</button>
            </div>
            <p class="repair-booking__search-help">Beschreibe nur das Symptom. Du musst nicht wissen, welches Bauteil defekt ist.</p>
          </form>

          ${quickSymptoms.length ? `
            <div class="repair-booking__quick">
              <p class="repair-booking__quick-title">Häufige Probleme</p>
              <div class="repair-booking__chips">
                ${quickSymptoms.map((term) => `<button type="button" class="repair-booking__chip" data-symptom="${this.escape(term)}">${this.escape(term)}</button>`).join('')}
                <button type="button" class="repair-booking__chip repair-booking__chip--muted" data-unclear>Fehler unklar</button>
              </div>
            </div>` : `
            <div class="repair-booking__quick">
              <button type="button" class="repair-booking__chip repair-booking__chip--muted" data-unclear>Fehler unklar</button>
            </div>`}
        </div>`;

      this.bindBack();
      const form = this.panel.querySelector('[data-search-form]');
      const input = form.querySelector('input[name="q"]');

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.search(String(input.value || '').trim());
      });

      this.panel.querySelectorAll('[data-symptom]').forEach((button) => {
        button.addEventListener('click', () => this.search(button.dataset.symptom));
      });

      const unclear = this.panel.querySelector('[data-unclear]');
      if (unclear) unclear.addEventListener('click', () => this.search('', true));

      requestAnimationFrame(() => input.focus({ preventScroll: true }));
    }

    scoreRepair(repair, query) {
      const normalizedQuery = this.normalize(query);
      if (!normalizedQuery) return 0;

      // Plattform und Gerät sind bereits durch repairPool() fest vorgegeben.
      // Sie dürfen deshalb nicht noch einmal als Symptom gewertet werden.
      const contextTokens = new Set([
        ...this.normalize(this.state.platform).split(' '),
        ...this.normalize(this.state.device).split(' ')
      ].filter(Boolean));

      // Wörter ohne diagnostischen Wert. So reicht z. B. "defekt" oder "ab"
      // nicht aus, um fachlich unpassende Reparaturen einzublenden.
      const ignoredTokens = new Set([
        ...contextTokens,
        'defekt', 'kaputt', 'fehler', 'problem', 'probleme',
        'reparatur', 'reparieren', 'service',
        'der', 'die', 'das', 'den', 'dem', 'des', 'ein', 'eine', 'einer',
        'und', 'oder', 'mit', 'ohne', 'von', 'vom', 'am', 'im', 'in', 'auf',
        'an', 'ab', 'aus', 'bei', 'beim', 'nach', 'vor', 'zur', 'zum',
        'ist', 'sind', 'wird', 'werden', 'sich', 'nicht', 'kein', 'keine',
        'dann', 'kurzer', 'kurze', 'kurzen', 'zeit'
      ]);

      const coreTokens = (value) => this.normalize(value)
        .split(' ')
        .filter((token) => token.length > 1 && !ignoredTokens.has(token));

      // Quick-Symptoms mit "/" sind Alternativen, z. B.
      // "Überhitzt / Lüfter laut" = Überhitzt ODER Lüfter laut.
      const queryAlternatives = String(query || '')
        .split(/\s*\/\s*/)
        .map((part) => ({ raw: this.normalize(part), tokens: coreTokens(part) }))
        .filter((part) => part.tokens.length > 0);

      if (!queryAlternatives.length) return 0;

      const symptomTerms = this.splitTerms(repair.symptoms)
        .map((term) => ({ raw: this.normalize(term), tokens: coreTokens(term) }))
        .filter((term) => term.tokens.length > 0);

      let symptomScore = 0;

      queryAlternatives.forEach((queryPart) => {
        symptomTerms.forEach((term) => {
          const queryPhrase = queryPart.tokens.join(' ');
          const termPhrase = term.tokens.join(' ');
          const termTokenSet = new Set(term.tokens);
          const allQueryTokensMatch = queryPart.tokens.every((token) => termTokenSet.has(token));

          let termScore = 0;

          if (queryPhrase === termPhrase) {
            termScore = 100;
          } else {
            const paddedTerm = ` ${termPhrase} `;
            const paddedQuery = ` ${queryPhrase} `;
            if (paddedTerm.includes(paddedQuery) || paddedQuery.includes(paddedTerm)) {
              termScore = 70;
            } else if (allQueryTokensMatch) {
              // Mehrwort-Suchen müssen vollständig im selben Symptom-Begriff
              // vorkommen. Dadurch matcht "USB Anschluss" nicht mehr nur auf
              // das allgemeine Wort "Anschluss" eines HDMI-Produkts.
              termScore = 45 + Math.min(queryPart.tokens.length, 4) * 5;
            }
          }

          symptomScore = Math.max(symptomScore, termScore);
        });
      });

      // Kein bestätigter Symptomtreffer = konkretes Produkt komplett raus.
      // Nur search() darf anschließend den expliziten Diagnose-Fallback laden.
      if (symptomScore <= 0) return 0;

      // Priorität dient ausschließlich der Sortierung fachlich passender Treffer.
      let score = symptomScore + Number(repair.priority || 0) * 0.05;
      const label = this.normalize(repair.label);
      const description = this.normalize(repair.shortDescription);
      const productTitle = this.normalize(repair.productTitle);
      const queryTokens = queryAlternatives.flatMap((part) => part.tokens);

      queryTokens.forEach((token) => {
        if (label.split(' ').includes(token)) score += 2;
        if (description.split(' ').includes(token)) score += 1;
        if (productTitle.split(' ').includes(token)) score += 1;
      });

      return score;
    }

    search(query, forceFallback = false) {
      const minLength = Number(this.dataset.minSearchLength || 2);
      if (!forceFallback && this.normalize(query).length < minLength) {
        this.showError(`Bitte gib mindestens ${minLength} Zeichen ein oder wähle einen häufigen Fehler.`);
        return;
      }

      this.clearError();
      this.state.query = query;
      const pool = this.repairPool();
      const scored = forceFallback
        ? []
        : pool
            .map((repair) => ({ repair, score: this.scoreRepair(repair, query) }))
            .filter((item) => item.score >= 4)
            .sort((a, b) => b.score - a.score || Number(b.repair.priority || 0) - Number(a.repair.priority || 0));

      let results = scored.map((item) => ({ ...item.repair, matchScore: item.score, fallback: false }));

      if (!results.length || forceFallback) {
        const fallback = pool
          .filter((repair) => repair.isFallback)
          .sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0))
          .map((repair) => ({ ...repair, matchScore: 0, fallback: true }));
        results = fallback;
      } else {
        const bestScore = results[0].matchScore;
        results = results.filter((repair, index) => index < 5 && repair.matchScore >= Math.max(4, bestScore * 0.32));
      }

      this.state.results = results;
      this.renderResults();
    }

    renderResults() {
      this.clearError();
      this.setProgress('results');
      const fallbackMode = !this.state.query || (this.state.results.length && this.state.results.every((r) => r.fallback));
      const noResults = this.state.results.length === 0;

      this.panel.innerHTML = `
        ${this.stepHead(
          noResults ? 'Kein passender Service gefunden' : (fallbackMode ? 'Wir empfehlen eine Diagnose' : 'Passende Reparaturen'),
          this.state.query ? `Gesucht: „${this.state.query}“` : `${this.state.platform} · ${this.state.device}`,
          'symptom'
        )}
        ${fallbackMode && !noResults ? `<div class="repair-booking__notice">${this.escape(this.dataset.noResultText || 'Der Fehler lässt sich nicht eindeutig zuordnen. Wir empfehlen eine Diagnose.')}</div>` : ''}
        ${noResults ? `
          <div class="repair-booking__empty-result">
            <p>Für dieses Gerät ist noch keine Diagnose als Fallback hinterlegt.</p>
            <button type="button" class="button button--secondary" data-back="symptom">Fehler anders beschreiben</button>
          </div>` : `
          <div class="repair-booking__result-grid">
            ${this.state.results.map((repair, index) => this.resultCard(repair, index)).join('')}
          </div>`}
      `;

      this.bindBack();
      this.panel.querySelectorAll('[data-select-repair]').forEach((button) => {
        button.addEventListener('click', () => {
          this.state.repairId = button.dataset.selectRepair;
          this.renderDetails();
        });
      });
    }

    resultCard(repair, index) {
      const available = repair.variants.filter((variant) => variant.available);
      const prices = available.length ? available.map((variant) => Number(variant.price)) : repair.variants.map((variant) => Number(variant.price));
      const startingPrice = prices.length ? Math.min(...prices) : 0;
      const unavailable = available.length === 0;
      const recommended = !repair.fallback && index === 0;
      const isDiagnosis = repair.paymentModel === 'diagnosis' || repair.paymentModel === 'deposit';

      return `
        <article class="repair-booking__result-card ${recommended ? 'is-recommended' : ''} ${unavailable ? 'is-unavailable' : ''}">
          ${repair.image ? `<div class="repair-booking__result-image"><img src="${this.escape(repair.image)}" alt="" loading="lazy"></div>` : ''}
          <div class="repair-booking__result-content">
            <div class="repair-booking__result-tags">
              ${recommended ? '<span class="repair-booking__badge">Beste Übereinstimmung</span>' : ''}
              ${repair.fallback ? '<span class="repair-booking__badge">Diagnose</span>' : ''}
              ${isDiagnosis ? `<span class="repair-booking__badge">${this.copyText('diagnosisBadgeText', 'Diagnose & Angebot')}</span>` : `<span class="repair-booking__badge">${this.copyText('fixedBadgeText', 'Festpreis')}</span>`}
              ${repair.badge ? `<span class="repair-booking__badge">${this.escape(repair.badge)}</span>` : ''}
            </div>
            <h3 class="h4 repair-booking__result-title">${this.escape(repair.label)}</h3>
            ${repair.shortDescription ? `<p class="repair-booking__result-description">${this.escape(repair.shortDescription)}</p>` : ''}
            ${isDiagnosis ? `
              <div class="repair-booking__payment-preview">
                <strong>Diagnosegebühr: ${this.money(startingPrice)}</strong>
                <span>${this.copyText('diagnosisPreviewText', 'Genaue Reparaturkosten nach Prüfung')}</span>
              </div>` : ''}
            <div class="repair-booking__result-bottom">
              <div class="repair-booking__result-price">
                ${isDiagnosis ? '<span class="repair-booking__price-label">Diagnosegebühr</span>' : ''}
                ${available.length > 1 ? 'ab ' : ''}${this.money(startingPrice)}
              </div>
              <button type="button" class="button button--primary" data-select-repair="${this.escape(repair.blockId)}" ${unavailable ? 'disabled' : ''}>
                ${unavailable ? 'Aktuell nicht verfügbar' : (isDiagnosis ? this.copyText('diagnosisSelectButton', 'Diagnose auswählen') : 'Reparatur auswählen')}
              </button>
            </div>
          </div>
        </article>`;
    }

    findSelectedRepair() {
      return this.repairs.find((repair) => repair.blockId === this.state.repairId);
    }

    matchesFilter(value, rawFilter) {
      const filters = this.splitTerms(rawFilter).map((item) => this.normalize(item));
      if (!filters.length) return true;
      const normalizedValue = this.normalize(value);
      return filters.includes('*') || filters.includes('alle') || filters.includes(normalizedValue);
    }

    availableAddons() {
      return this.addons.filter((addon) => {
        const platformMatches = this.matchesFilter(this.state.platform, addon.platforms);
        const deviceMatches = this.matchesFilter(this.state.device, addon.devices);
        const hasAvailableVariant = Array.isArray(addon.variants) && addon.variants.some((variant) => variant.available);
        return platformMatches && deviceMatches && hasAvailableVariant;
      });
    }

    groupAddons(addons) {
      const groups = new Map();
      addons.forEach((addon) => {
        const groupName = String(addon.group || 'Zusatzleistungen').trim() || 'Zusatzleistungen';
        if (!groups.has(groupName)) groups.set(groupName, []);
        groups.get(groupName).push(addon);
      });
      return [...groups.entries()].map(([name, items]) => ({ name, items }));
    }

    addonOptionLabel(addon, variant) {
      const variantTitle = String(variant.title || '').trim();
      if (!variantTitle || variantTitle === 'Default Title') return addon.label;
      return `${addon.label} – ${variantTitle}`;
    }

    selectedAddonEntries(form) {
      return [...form.querySelectorAll('[data-addon-input]:checked')]
        .filter((input) => Number(input.value) > 0)
        .map((input) => ({
          variantId: Number(input.value),
          label: input.dataset.addonLabel || 'Zusatzleistung',
          group: input.dataset.addonGroup || 'Zusatzleistungen',
          price: Number(input.dataset.addonPrice || 0)
        }));
    }

    updateDetailTotals(form, repair, isDiagnosis) {
      const variantField = form.elements.variant;
      let mainPrice = 0;

      if (variantField?.tagName === 'SELECT') {
        mainPrice = Number(variantField.selectedOptions[0]?.dataset.price || 0);
      } else {
        const selectedVariant = repair.variants.find((variant) => Number(variant.id) === Number(variantField?.value));
        mainPrice = Number(selectedVariant?.price || 0);
      }

      const addons = this.selectedAddonEntries(form);
      const addonTotal = addons.reduce((sum, addon) => sum + addon.price, 0);
      const total = mainPrice + addonTotal;

      const mainPriceNode = this.panel.querySelector('[data-summary-main-price]');
      if (mainPriceNode) mainPriceNode.textContent = this.money(mainPrice);

      const addonSummary = this.panel.querySelector('[data-summary-addons]');
      if (addonSummary) {
        addonSummary.hidden = addons.length === 0;
        addonSummary.innerHTML = addons.map((addon) => `
          <div class="repair-booking__summary-row">
            <span>${this.escape(addon.label)}</span>
            <strong>${this.money(addon.price)}</strong>
          </div>`).join('');
      }

      const totalNode = this.panel.querySelector('[data-summary-total]');
      if (totalNode) totalNode.textContent = this.money(total);

      const paymentInitial = this.panel.querySelector('[data-payment-initial]');
      if (paymentInitial) {
        paymentInitial.textContent = isDiagnosis
          ? this.copyRaw('diagnosisInitialText', 'Du zahlst zunächst nur die Diagnosegebühr von {price}.', { price: this.money(mainPrice) })
          : this.copyRaw('fixedInitialText', 'Der angezeigte Preis von {price} gilt für die ausgewählte Reparatur.', { price: this.money(mainPrice) });
      }

      const paymentCredit = this.panel.querySelector('[data-payment-credit]');
      if (paymentCredit) {
        paymentCredit.textContent = isDiagnosis
          ? this.copyRaw('diagnosisCreditText', 'Entscheidest du dich für die Reparatur, wird die bereits gezahlte Diagnosegebühr von {price} vollständig angerechnet.', { price: this.money(mainPrice) })
          : this.copyRaw('fixedCreditText', 'Der bereits gezahlte Betrag von {price} wird vollständig auf den neuen Gesamtpreis angerechnet.', { price: this.money(mainPrice) });
      }

      const submit = form.querySelector('[data-submit]');
      const baseButtonText = isDiagnosis ? this.copyRaw('diagnosisCartButton', 'Diagnoseauftrag in den Warenkorb') : 'Reparatur in den Warenkorb';
      if (submit && addons.length) {
        submit.textContent = `${baseButtonText} · ${this.money(total)}`;
      } else if (submit) {
        submit.textContent = baseButtonText;
      }
    }

    renderAddonGroups(groups) {
      if (!groups.length) return '';

      return `
        <section class="repair-booking__addons" aria-labelledby="RepairAddonsHeading-${this.dataset.sectionId}">
          <div class="repair-booking__addons-head">
            <h3 id="RepairAddonsHeading-${this.dataset.sectionId}" class="h4">${this.copyText('addonsHeading', 'Optionale Zusatzleistungen')}</h3>
            <p>${this.copyText('addonsIntro', 'Ergänze deinen Reparaturauftrag auf Wunsch um eine Reinigung oder einen passenden Wartungsservice.')}</p>
          </div>
          ${groups.map((group, groupIndex) => `
            <fieldset class="repair-booking__addon-group">
              <legend>${this.escape(group.name)}</legend>
              <div class="repair-booking__addon-options">
                <label class="repair-booking__addon-card">
                  <input type="radio" name="addon_${groupIndex}" value="" checked data-addon-input>
                  <span class="repair-booking__addon-card-body">
                    <span class="repair-booking__addon-title">${this.copyText('addonsNoneLabel', 'Keine Zusatzleistung')}</span>
                    <span class="repair-booking__addon-price">0,00 €</span>
                  </span>
                </label>
                ${group.items.map((addon) => addon.variants
                  .filter((variant) => variant.available)
                  .map((variant) => {
                    const label = this.addonOptionLabel(addon, variant);
                    return `
                      <label class="repair-booking__addon-card">
                        <input
                          type="radio"
                          name="addon_${groupIndex}"
                          value="${variant.id}"
                          data-addon-input
                          data-addon-label="${this.escape(label)}"
                          data-addon-group="${this.escape(group.name)}"
                          data-addon-price="${variant.price}"
                        >
                        <span class="repair-booking__addon-card-body">
                          <span class="repair-booking__addon-title-row">
                            <span class="repair-booking__addon-title">${this.escape(label)}</span>
                            ${addon.badge ? `<span class="repair-booking__badge">${this.escape(addon.badge)}</span>` : ''}
                          </span>
                          ${addon.description ? `<span class="repair-booking__addon-description">${this.escape(addon.description)}</span>` : ''}
                          <span class="repair-booking__addon-price">+ ${this.money(variant.price)}</span>
                        </span>
                      </label>`;
                  }).join('')).join('')}
              </div>
            </fieldset>`).join('')}
        </section>`;
    }

    renderDetails() {
      this.clearError();
      this.setProgress('details');
      const repair = this.findSelectedRepair();
      if (!repair) return this.renderResults();

      const availableVariants = repair.variants.filter((variant) => variant.available);
      const firstVariant = availableVariants[0];
      if (!firstVariant) {
        this.showError('Diese Reparatur ist aktuell nicht verfügbar.');
        return this.renderResults();
      }

      const showVariantPicker = repair.variants.length > 1;
      const showSerial = this.dataset.showSerial === 'true';
      const showOpened = this.dataset.showOpened === 'true';
      const showDescription = this.dataset.showDescription === 'true';
      const isDiagnosis = repair.paymentModel === 'diagnosis' || repair.paymentModel === 'deposit';
      const addonGroups = this.groupAddons(this.availableAddons());
      const hasAddons = addonGroups.length > 0;

      const paymentBox = isDiagnosis ? `
        <div class="repair-booking__payment-box">
          <strong>${this.copyText('paymentHeading', 'Wichtiger Hinweis zum Reparaturpreis')}</strong>
          <p data-payment-initial>${this.copyText('diagnosisInitialText', 'Du zahlst zunächst nur die Diagnosegebühr von {price}.', { price: this.money(firstVariant.price) })}</p>
          <p>${this.copyText('diagnosisQuoteText', 'Nach der Prüfung teilen wir dir die genaue Ursache und den verbindlichen Reparaturpreis mit.')}</p>
          <p data-payment-credit>${this.copyText('diagnosisCreditText', 'Entscheidest du dich für die Reparatur, wird die bereits gezahlte Diagnosegebühr von {price} vollständig angerechnet.', { price: this.money(firstVariant.price) })}</p>
          <p>${this.copyText('diagnosisApprovalText', 'Ohne deine ausdrückliche Zustimmung führen wir keine Reparaturarbeiten durch.')}</p>
          ${hasAddons ? `<p class="repair-booking__addon-payment-note">${this.copyText('addonsPaymentNote', 'Optionale Zusatzleistungen werden unabhängig vom Reparatur- oder Diagnosepreis zum jeweils angezeigten Festpreis berechnet.')}</p>` : ''}
        </div>` : `
        <div class="repair-booking__payment-box">
          <strong>${this.copyText('paymentHeading', 'Wichtiger Hinweis zum Reparaturpreis')}</strong>
          <p data-payment-initial>${this.copyText('fixedInitialText', 'Der angezeigte Preis von {price} gilt für die ausgewählte Reparatur.', { price: this.money(firstVariant.price) })}</p>
          <p>${this.copyText('fixedSameFaultText', 'Stellt sich bei der Prüfung heraus, dass ausschließlich der ausgewählte Defekt vorliegt, bleibt es bei diesem Gesamtpreis. Es entstehen keine weiteren Reparaturkosten.')}</p>
          <p>${this.copyText('fixedAdditionalFaultText', 'Sollte zusätzlich oder anstelle des ausgewählten Defekts ein anderer Fehler vorliegen, pausieren wir die Reparatur und teilen dir den genauen zusätzlichen Reparaturpreis mit.')}</p>
          <p data-payment-credit>${this.copyText('fixedCreditText', 'Der bereits gezahlte Betrag von {price} wird vollständig auf den neuen Gesamtpreis angerechnet.', { price: this.money(firstVariant.price) })}</p>
          <p>${this.copyText('fixedApprovalText', 'Zusätzliche Arbeiten führen wir ausschließlich nach deiner ausdrücklichen Zustimmung durch. Ohne deine Zustimmung entstehen keine Kosten über den bereits gebuchten Reparaturpreis hinaus.')}</p>
          ${hasAddons ? `<p class="repair-booking__addon-payment-note">${this.copyText('addonsPaymentNote', 'Optionale Zusatzleistungen werden unabhängig vom Reparatur- oder Diagnosepreis zum jeweils angezeigten Festpreis berechnet.')}</p>` : ''}
        </div>`;

      this.panel.innerHTML = `
        ${this.stepHead('Auftrag vervollständigen', `${this.state.platform} · ${this.state.device}`, 'results')}
        <div class="repair-booking__details-layout">
          <form class="repair-booking__form" data-repair-form>
            ${showVariantPicker ? `
              <div class="repair-booking__field">
                <label for="RepairVariant-${this.dataset.sectionId}">Ausführung</label>
                <select class="repair-booking__select" id="RepairVariant-${this.dataset.sectionId}" name="variant">
                  ${repair.variants.map((variant) => `
                    <option value="${variant.id}" data-price="${variant.price}" ${variant.available ? '' : 'disabled'} ${variant.id === firstVariant.id ? 'selected' : ''}>
                      ${this.escape(variant.title)} – ${this.money(variant.price)}${variant.available ? '' : ' – nicht verfügbar'}
                    </option>`).join('')}
                </select>
              </div>` : `<input type="hidden" name="variant" value="${firstVariant.id}">`}

            ${showSerial ? `
              <div class="repair-booking__field">
                <label for="RepairSerial-${this.dataset.sectionId}">Seriennummer <small>(optional)</small></label>
                <input class="repair-booking__input" id="RepairSerial-${this.dataset.sectionId}" name="serial" type="text" autocomplete="off" placeholder="z. B. S01-…">
              </div>` : ''}

            ${showOpened ? `
              <fieldset class="repair-booking__fieldset">
                <legend>Wurde das Gerät bereits geöffnet oder repariert?</legend>
                <div class="repair-booking__radio-group">
                  <label class="repair-booking__radio"><input type="radio" name="opened" value="Nein" checked><span>Nein</span></label>
                  <label class="repair-booking__radio"><input type="radio" name="opened" value="Ja"><span>Ja</span></label>
                </div>
              </fieldset>` : ''}

            ${showDescription ? `
              <div class="repair-booking__field">
                <label for="RepairDescription-${this.dataset.sectionId}">Fehlerbeschreibung <small>(optional)</small></label>
                <textarea class="repair-booking__textarea" id="RepairDescription-${this.dataset.sectionId}" name="description" placeholder="Beschreibe kurz, was passiert ist und welche Symptome auftreten.">${this.escape(this.state.query)}</textarea>
              </div>` : ''}

            ${this.renderAddonGroups(addonGroups)}
            ${paymentBox}

            <button type="submit" class="button button--primary repair-booking__submit" data-submit>${isDiagnosis ? this.copyText('diagnosisCartButton', 'Diagnoseauftrag in den Warenkorb') : 'Reparatur in den Warenkorb'}</button>
          </form>

          <aside class="repair-booking__summary">
            ${repair.image ? `<div class="repair-booking__summary-image"><img src="${this.escape(repair.image)}" alt="" loading="lazy"></div>` : ''}
            <div class="repair-booking__summary-content">
              <p class="repair-booking__summary-kicker">Dein Auftrag</p>
              <h3 class="h4">${this.escape(repair.label)}</h3>
              <p class="repair-booking__summary-model">${this.escape(this.state.platform)} · ${this.escape(this.state.device)}</p>
              ${this.state.query ? `<p class="repair-booking__summary-query"><strong>Fehler:</strong> ${this.escape(this.state.query)}</p>` : ''}
              <p class="repair-booking__summary-payment"><strong>${isDiagnosis ? this.copyText('diagnosisBadgeText', 'Diagnose & Angebot') : this.copyText('fixedBadgeText', 'Festpreis')}</strong></p>
              <div class="repair-booking__summary-breakdown">
                <div class="repair-booking__summary-row">
                  <span>${isDiagnosis ? 'Diagnosegebühr' : 'Reparatur'}</span>
                  <strong data-summary-main-price>${this.money(firstVariant.price)}</strong>
                </div>
                <div data-summary-addons hidden></div>
                <div class="repair-booking__summary-row repair-booking__summary-row--total">
                  <span>Heute insgesamt</span>
                  <strong data-summary-total>${this.money(firstVariant.price)}</strong>
                </div>
              </div>
            </div>
          </aside>
        </div>`;

      this.bindBack();
      const form = this.panel.querySelector('[data-repair-form]');
      const variantField = form.elements.variant;
      if (variantField && variantField.tagName === 'SELECT') {
        variantField.addEventListener('change', () => this.updateDetailTotals(form, repair, isDiagnosis));
      }
      form.querySelectorAll('[data-addon-input]').forEach((input) => {
        input.addEventListener('change', () => this.updateDetailTotals(form, repair, isDiagnosis));
      });

      this.updateDetailTotals(form, repair, isDiagnosis);
      form.addEventListener('submit', (event) => this.submitRepair(event, repair));
    }

    async submitRepair(event, repair) {
      event.preventDefault();
      this.clearError();
      const form = event.currentTarget;
      const submit = form.querySelector('[data-submit]');
      const formData = new FormData(form);
      const variantId = Number(formData.get('variant'));

      if (!variantId) {
        this.showError('Bitte wähle eine verfügbare Ausführung.');
        return;
      }

      const isDiagnosis = repair.paymentModel === 'diagnosis' || repair.paymentModel === 'deposit';
      const selectedAddons = this.selectedAddonEntries(form);
      const properties = {
        'Plattform': this.state.platform,
        'Gerät / Modell': this.state.device,
        'Gesuchter Fehler': this.state.query || 'Fehler unklar',
        'Gewählte Reparatur': repair.label,
        'Auftragsart': isDiagnosis ? 'Diagnose – Angebot nach Prüfung' : 'Festpreis – Zusatzarbeiten nur nach Freigabe'
      };

      if (isDiagnosis) {
        properties['Anrechnung'] = 'Diagnosegebühr wird bei Annahme vollständig auf den Reparaturpreis angerechnet';
        properties['Freigabe'] = 'Reparatur erst nach ausdrücklicher Zustimmung des Kunden';
      } else {
        properties['Zusatzfehler'] = 'Bei anderem oder zusätzlichem Defekt Reparatur pausieren und exakten Zusatzpreis anfragen';
        properties['Freigabe'] = 'Keine Zusatzarbeiten ohne ausdrückliche Zustimmung des Kunden';
      }

      if (selectedAddons.length) {
        properties['Zusatzleistungen'] = selectedAddons.map((addon) => addon.label).join(', ');
        properties['Hinweis Zusatzleistungen'] = 'Zum jeweils angezeigten Festpreis berechnet';
      }

      if (formData.get('serial')) properties['Seriennummer'] = String(formData.get('serial')).trim();
      if (formData.get('opened')) properties['Bereits geöffnet / repariert'] = formData.get('opened');
      if (formData.get('description')) properties['Fehlerbeschreibung'] = String(formData.get('description')).trim();

      Object.keys(properties).forEach((key) => {
        if (!properties[key]) delete properties[key];
      });

      const items = [{ id: variantId, quantity: 1, properties }];
      selectedAddons.forEach((addon) => {
        items.push({
          id: addon.variantId,
          quantity: 1,
          properties: {
            'Zusatzleistung für': `${this.state.platform} · ${this.state.device}`,
            'Zugehörige Reparatur': repair.label,
            'Gesuchter Fehler': this.state.query || 'Fehler unklar'
          }
        });
      });

      submit.disabled = true;
      const originalText = submit.textContent;
      submit.textContent = 'Wird hinzugefügt …';

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Die Reparatur konnte nicht hinzugefügt werden.');

        window.location.href = this.dataset.cartMode === 'checkout' ? `${root}checkout` : `${root}cart`;
      } catch (error) {
        console.error(error);
        submit.disabled = false;
        submit.textContent = originalText;
        this.showError(error.message || 'Die Reparatur konnte nicht hinzugefügt werden. Bitte versuche es erneut.');
      }
    }

    showError(message) {
      if (!this.errorBox) return;
      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
    }

    clearError() {
      if (!this.errorBox) return;
      this.errorBox.textContent = '';
      this.errorBox.hidden = true;
    }
  }

  if (!customElements.get('repair-booking')) customElements.define('repair-booking', RepairBooking);
})();
